<template>
    <div class="stepper-container">
        <div class="stepper-wrapper">
            <div v-for="(step, index) in props.steps" :key="index" class="step-item" :class="getStepClass(index)" @click="handleStepClick(index)">
                <div class="step-circle">
                    <i v-if="index < props.modelValue" class="pi pi-check"></i>
                </div>
                <div class="step-label">
                    {{ step.label }}
                </div>
                <div
                    v-if="index < props.steps.length - 1"
                    class="step-connector"
                    :class="{
                        completed: index + 1 < props.modelValue,
                        active: index + 1 === props.modelValue,
                    }"
                ></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
    modelValue: {
        type: Number,
        default: 0,
    },
    steps: {
        type: Array,
        required: true,
    },
    clickable: {
        type: Boolean,
        default: true,
    },
});

const emit = defineEmits(['update:modelValue']);

const getStepClass = (index) => {
    if (index < props.modelValue) return 'completed';
    if (index === props.modelValue) return 'active';
    return '';
};

const handleStepClick = (index) => {
    if (props.clickable) {
        emit('update:modelValue', index);
    }
};
</script>

<style scoped lang="scss">
.stepper-container {
    width: auto;
    padding: 0;
    display: inline-flex;
    justify-content: flex-start;
}

.stepper-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    position: relative;
    gap: 0;
}

.step-item {
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-right: 0;
}

.step-item:last-child {
    margin-right: 0;
}

.step-circle {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 8px;
    margin-right: 8px;
    margin-left: 8px;
    transition: all 0.3s ease;
    position: relative;
    z-index: 2;
    flex-shrink: 0;
    background-color: #e5e7eb;
    color: #6b7280;
}

.step-circle .pi.pi-check {
    font-size: 6px;
    line-height: 1;
}

.step-item.active .step-circle {
    background-color: #3b82f6;
    color: white;
}

.step-item.completed .step-circle {
    background-color: #10b981;
    color: white;
}

.step-label {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    transition: color 0.3s ease;
    white-space: nowrap;

    &:not(:last-child) {
        margin-right: 40px;
    }

    &:last-child {
        padding-right: 12px;
    }
}

.step-item.active .step-label {
    color: #3b82f6;
    font-weight: 600;
}

.step-item.completed .step-label {
    color: #10b981;
    font-weight: 600;
}

.step-connector {
    position: absolute;
    top: 50%;
    left: calc(100% - 32px);
    width: 32px;
    height: 2px;
    background-color: var(--primary-color);
    border-radius: 99999px;
    z-index: 1;
    transform: translateY(-50%);
    transition: background-color 0.3s ease;
}

.step-connector.active {
    background-color: #2196f3;
}

.step-connector.completed {
    background-color: #22c55e;
}

@media (max-width: 768px) {
    .step-label {
        margin-right: 24px;
        font-size: 13px;
    }

    .step-circle {
        width: 1rem;
        height: 1rem;
        font-size: 6px;
        margin-right: 6px;
    }

    .step-connector {
        left: calc(100% - 20px);
        width: 20px;
    }
}

.step-item:hover .step-circle:not(.active):not(.completed) {
    background-color: #9ca3af;
}

.step-item:hover .step-label:not(.active):not(.completed) {
    color: #4b5563;
}
</style>
